// Full refactored component with batch CSV upload via JSON
import React, { useRef, useEffect, useState } from "react";
import Papa from "papaparse";
import makeStyles from "@mui/styles/makeStyles";
import {
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Grid,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CSVLink } from "react-csv";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  appBarSpacer: theme.mixins.toolbar,
  appBar: { position: "fixed" },
  title: { marginLeft: theme.spacing(2), flex: 1 },
  csvInput: { display: "none" },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CHUNK_SIZE = 100;

function ViewAccountInformation(props) {
  const classes = useStyles();
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [isSavingSuccess, setIsSavingSuccess] = useState(false);
  const [uploadingMessage, setUploadingMessage] = useState("");
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const selectFileElement = useRef();
  const downloadCsvTemplateRef = useRef();
  const [addStockCsvTemplate, setAddStockCsvTemplate] = useState([]);
  const defaultCSVTemplate = [
    props.accountsLabel.singular + " Name",
    props.accountsLabel.singular + " Code",
    props.accountClassificationLabel.singular + " Name",
    "Province",
    "City/Municipality",
    "Barangay",
    "Street",
    "First name",
    "Middle name",
    "Last name",
    "Contact Number",
  ];

  useEffect(() => {
    getAccountCSVTemplate();
  }, []);

  const getAccountCSVTemplate = async () => {
    try {
      const response = await axios.post(
        "/SellerAccounts/getAccountRequiredFieldSettings.json",
        { search_term: "" }
      );
      const account_field_settings =
        response.data.response.account_field_settings;

      const csvHeader = defaultCSVTemplate.map((template) => {
        let header = template
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/\//g, "_");
        if (header === "sub-account_name") header = "account_name";
        if (header === "sub-account_code") header = "account_code";
        if (["account_classification_name", "channel_name"].includes(header))
          header = "classification";
        return account_field_settings.includes(header)
          ? template + "*"
          : template;
      });

      setAddStockCsvTemplate(csvHeader);
    } catch (error) {
      setMessage(error.message);
      setShowMessageDialog(true);
    }
  };

  const uploadCSVInBatches = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const allRows = results.data;
        const totalChunks = Math.ceil(allRows.length / CHUNK_SIZE);

        const uploadChunk = async (chunkIndex = 0) => {
          if (chunkIndex >= totalChunks) {
            setMessage("All data uploaded successfully.");
            setIsSavingSuccess(true);
            setIsUploadSuccess(true);
            setShowMessageDialog(true);
            return;
          }

          const chunk = allRows.slice(
            chunkIndex * CHUNK_SIZE,
            (chunkIndex + 1) * CHUNK_SIZE
          );

          setUploadingMessage(
            `Uploading chunk ${chunkIndex + 1} of ${totalChunks}...`
          );
          setUploadProgress(Math.round(((chunkIndex + 1) / totalChunks) * 100));
          try {
            const res = await axios.post(
              "/SellerAccounts/createAccountViaCsv.json",
              {
                accounts_batch: chunk,
              }
            );

            if (!res.data.response?.is_success)
              throw new Error(res.data.response.message || "Upload failed");

            uploadChunk(chunkIndex + 1);
          } catch (error) {
            setIsSavingAccount(false);
            setMessage(
              "Batch upload failed: " + (error.message || "Unknown error")
            );
            setIsUploadSuccess(false);
            setShowMessageDialog(true);
          }
        };

        setIsSavingAccount(true);
        uploadChunk(0);
      },
      error: function (err) {
        setMessage("CSV parsing error: " + err.message);
        setShowMessageDialog(true);
      },
    });
  };

  const messageDialog = () => (
    <Dialog fullWidth maxWidth="sm" open={showMessageDialog}>
      <DialogTitle>Message</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ color: isSavingSuccess ? "" : "red" }}>
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setShowMessageDialog(false);
            if (isSavingSuccess) props.setShowCreateAccountViaCsvDialog(false);
          }}
          color="primary"
          autoFocus
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const uploadProgressElement = () => (
    <div style={{ padding: "1rem", display: isSavingAccount ? "" : "none" }}>
      <Typography variant="h6">{uploadingMessage}</Typography>
      <LinearProgress variant="determinate" value={uploadProgress} />
    </div>
  );

  const mainBody = () => (
    <Grid
      container
      spacing={1}
      style={{ display: isSavingAccount ? "none" : "" }}
    >
      <Grid item xs={12} sm={6}>
        <CSVLink
          data={[addStockCsvTemplate]}
          ref={downloadCsvTemplateRef}
          filename={`${props.accountsLabel.plural}.csv`}
        >
          <Button variant="contained" color="secondary" fullWidth>
            Download template
          </Button>
        </CSVLink>
      </Grid>
      <Grid item xs={12} sm={6}>
        <input
          accept=".csv"
          ref={selectFileElement}
          className={classes.csvInput}
          type="file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            uploadCSVInBatches(file);
          }}
        />
        <label>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => selectFileElement.current.click()}
          >
            Select File
          </Button>
        </label>
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      fullScreen
      open={props.showCreateAccountViaCsvDialog}
      onClose={() => props.setShowCreateAccountViaCsvDialog(false)}
      TransitionComponent={Transition}
    >
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => props.setShowCreateAccountViaCsvDialog(false)}
            aria-label="close"
            size="large"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle1" className={classes.title}>
            Upload {props.accountsLabel.plural} via csv
          </Typography>
        </Toolbar>
      </AppBar>
      <div className={classes.appBarSpacer}></div>
      <div style={{ padding: ".5rem" }}>
        {uploadProgressElement()}
        {mainBody()}
      </div>
      {messageDialog()}
    </Dialog>
  );
}

export default ViewAccountInformation;
